import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePublicPage } from '../../hooks/usePage';
import PageEditor from './PageEditor';
import SkeletonPage from '../Skeletons/SkeletonPage';
import { Lock, Public } from '../icons';
import './scss/PublicPageViewer.scss';

/**
 * Public Page Viewer
 * Displays a page shared via public link (no authentication required)
 */
const PublicPageViewer = () => {
  const { pageId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { data, isLoading, error } = usePublicPage(pageId, token);

  // Show skeleton while loading OR if we don't have data/error yet
  if (isLoading || (!data && !error)) {
    return <SkeletonPage />;
  }

  // Only show error if loading is finished AND there's actually an error or no page
  if (error || !data?.page) {
    return (
      <div className="public-page-error">
        <div className="error-icon">
          <Lock size={48} />
        </div>
        <h2>Page not found</h2>
        <p>
          {error?.response?.status === 403
            ? 'This page is private or the link has expired.'
            : 'The page you\'re looking for doesn\'t exist or has been removed.'}
        </p>
      </div>
    );
  }

  const page = data.page;

  return (
    <div className={`public-page-viewer ${page.coverImage ? 'has-cover' : 'no-cover'}`}>
      {/* Top Navigation Bar */}
      <div className="public-page-top-bar">
        <div className="public-page-top-bar-left">
          <div className="public-page-top-info">
            <span className="public-page-top-icon">{page.icon || '📄'}</span>
            <h1 className="public-page-top-title">{page.title || 'Untitled'}</h1>
          </div>
          <div className="public-page-top-badge">
            <Public size={14} />
            <span>Public</span>
          </div>
        </div>
        <div className="public-page-top-bar-right">
          <div className="public-page-owner">
            Shared by {page.ownerUserId?.name || page.ownerUserId?.username}
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {page.coverImage && (
        <div className="public-page-cover-container">
          <div 
            className="public-page-cover"
            style={{
              backgroundImage: `url("${typeof page.coverImage === 'string' ? page.coverImage : page.coverImage.url}")`,
              backgroundSize: 'cover',
              backgroundPosition: `center ${typeof page.coverImage === 'object' ? (page.coverImage.position || 50) : 50}%`
            }}
          />
        </div>
      )}

      {/* Page Content */}
      <div className="public-page-content">
        <div className="public-page-container">
          {/* Icon */}
          {page.icon && (
            <div className="public-page-icon-large">
              {page.icon}
            </div>
          )}
          
          {/* Title */}
          <h1 className="public-page-title">
            {page.title || 'Untitled'}
          </h1>

          {/* Editor (Read-only) */}
          <div className="public-page-editor">
            <PageEditor
              key={page._id}
              pageId={page._id}
              initialContent={page.content}
              onContentChange={() => {}} // Read-only, no changes allowed
              readOnly={true}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="public-page-footer">
        <div className="public-page-footer-content">
          <p>
            This page is publicly shared. 
            <a href="https://zoobbe.com" target="_blank" rel="noopener noreferrer">
              Create your own workspace
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicPageViewer;

